import axios from 'axios';
import React, { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PinDialog.scss';

interface PinDialogProps {
    open: boolean;
    onClose: () => void;
}

const API_URL = process.env.REACT_APP_API_URL;

const PinDialog: React.FC<PinDialogProps> = ({ open, onClose }) => {
    const [pinDigits, setPinDigits] = useState(['', '', '', '']);
    const [locked, setLocked] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [step, setStep] = useState<'pin' | 'tvNumber' | 'rememberPin'>('pin');
    const [tvNumber, setTvNumber] = useState(['', '']);
    const [companyId, setCompanyId] = useState("");
    const [tvIds, setTvIds] = useState<number[]>([]);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    const skipToPlay = useCallback((companyId: string, tvId: string, tvIds: number[] = []) => {
        setLocked(true);
        setCompanyId(companyId);
        setTvIds(tvIds);

        requestFullScreen();

        navigate('/play', { state: { companyId: companyId, tvId: parseInt(tvId) } });
        onClose();
    }, [navigate, onClose]);

    useEffect(() => {
        // Parse query parameters from URL
        const queryParams = new URLSearchParams(location.search);
        const urlPin = queryParams.get('pin');
        const urlTvId = queryParams.get('tvId');
        const urlCompanyId = queryParams.get('companyId');

        // If we have all necessary parameters from URL
        if (urlPin && urlTvId && urlCompanyId) {
            // Check with backend if we should remember PIN
            const checkRememberPin = async () => {
                try {
                    const response = await axios.get(`${API_URL}/tv/getRememberPin`, {
                        params: { tvId: urlTvId }
                    });
                    
                    const rememberPin = response.data.rememberPin;
                    
                    // If we should remember PIN according to backend, skip to play
                    if (rememberPin === true) {
                        skipToPlay(urlCompanyId, urlTvId);
                    }
                } catch (error) {
                    console.error("Error checking remember PIN:", error);
                    // Continue to show PIN dialog on error
                }
            };
            
            checkRememberPin();
        } else {
            // Fallback to check localStorage for compatibility
            const storedPin = localStorage.getItem('pin');
            const storedTvNumber = localStorage.getItem('tvNumber');
            const storedCompanyId = localStorage.getItem('companyId');
            const storedTvIds = localStorage.getItem('tvIds') ? JSON.parse(localStorage.getItem('tvIds') || '[]') : [];

            if (storedPin && storedTvNumber && storedCompanyId && storedTvIds.length) {
                skipToPlay(storedCompanyId, storedTvNumber, storedTvIds);
            }
        }
    }, [skipToPlay, location.search]);

    useEffect(() => {
        if (open && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [open, step]);

    async function verifyPin(pin: string) {
        try {
            const response = await axios.post(API_URL + '/company/validatePin', {
                pin: pin
            });
            setCompanyId(response.data.companyId);
            setTvIds(response.data.tvIds); // Store TV IDs
            return response.data.companyId;
        } catch (error: any) {
            if (error.response) {
                // Handle 401 and 500 status codes
                if (error.response.status === 401 || error.response.status === 500) {
                    return undefined;
                }
            }
            console.error("Error during PIN validation: ", error);
            return undefined; // Return false for any other kind of error
        }
    }

    const handleInputChange = async (index: number, value: string) => {
        if (locked) return;

        // Only allow digits
        if (value && !/^\d+$/.test(value)) return;

        const newPinDigits = [...pinDigits];
        newPinDigits[index] = value;
        setPinDigits(newPinDigits);

        // Auto-advance to next input when a digit is entered
        if (value && index < 3) {
            const nextInputRef = inputRefs.current[index + 1];
            if (nextInputRef) {
                nextInputRef.focus();
            }
        }

        // Check if PIN is complete
        if (index === 3 && newPinDigits.every(digit => digit.length === 1)) {
            setLocked(true);
            const pin = newPinDigits.join('');
            let companyIdVerified = await verifyPin(pin);
            if (!companyIdVerified) {
                setErrorMessage('PIN is not correct');
                setPinDigits(['', '', '', '']);
                setLocked(false);
                setTimeout(() => {
                    inputRefs.current[0]?.focus();
                }, 0);
                setTimeout(() => {
                    setErrorMessage('');
                }, 5000);
            } else {
                setLocked(false);
                setStep('tvNumber'); // Move to the TV number step
            }
        }
    };

    // Handle keyboard events for PIN and TV number input
    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>, currentState: string[], setState: React.Dispatch<React.SetStateAction<string[]>>, maxIndex: number) => {
        if (locked) return;

        // Handle backspace
        if (e.key === 'Backspace') {
            const newState = [...currentState];
            
            if (newState[index] === '') {
                // If current box is empty, move to previous box and clear it
                if (index > 0) {
                    newState[index - 1] = '';
                    setState(newState);
                    inputRefs.current[index - 1]?.focus();
                }
            } else {
                // Clear current box
                newState[index] = '';
                setState(newState);
            }
        } 
        // Handle left arrow key
        else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } 
        // Handle right arrow key
        else if (e.key === 'ArrowRight' && index < maxIndex) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleTvNumberChange = (index: number, value: string) => {
        if (locked) return;

        // Only allow digits
        if (value && !/^\d+$/.test(value)) return;

        const newTvNumber = [...tvNumber];
        newTvNumber[index] = value;
        setTvNumber(newTvNumber);

        // Auto-advance to next input when a digit is entered
        if (value && index < 1) {
            const nextInputRef = inputRefs.current[index + 1];
            if (nextInputRef) {
                nextInputRef.focus();
            }
        }

        // Check if TV number is complete
        if (index === 1 && newTvNumber.every(digit => digit.length === 1)) {
            // Validate TV number against available IDs
            const tvIndex = parseInt(newTvNumber.join('')) - 1;
            if (tvIndex >= 0 && tvIndex < tvIds.length) {
                setStep('rememberPin'); // Move to remember PIN step
            } else {
                setErrorMessage('Invalid TV number');
                setTvNumber(['', '']);
                setTimeout(() => {
                    inputRefs.current[0]?.focus();
                }, 0);
                setTimeout(() => {
                    setErrorMessage('');
                }, 5000);
            }
        }
    };

    // Function to request fullscreen mode
    const requestFullScreen = () => {
        const element = document.documentElement;
        if (element.requestFullscreen) {
            element.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        }
    };

    // New handler for the remember PIN step
    const handleRememberChoice = async (remember: boolean) => {
        const selectedTvId = tvIds[parseInt(tvNumber.join('')) - 1];

        try {
            // Update remember PIN setting in backend
            await axios.post(`${API_URL}/tv/updateRememberPin`, {
                tvId: selectedTvId,
                rememberPin: remember
            });

            // For backward compatibility, still use localStorage
            if (remember) {
                localStorage.setItem('pin', pinDigits.join(''));
                localStorage.setItem('tvNumber', selectedTvId.toString());
                localStorage.setItem('companyId', companyId);
                localStorage.setItem('tvIds', JSON.stringify(tvIds));
            } else {
                // Clear localStorage if not remembering
                localStorage.removeItem('pin');
                localStorage.removeItem('tvNumber');
                localStorage.removeItem('companyId');
                localStorage.removeItem('tvIds');
            }
        } catch (error) {
            console.error("Error updating remember PIN setting:", error);
        }

        // Request fullscreen before navigating
        requestFullScreen();

        // Navigate to play component
        navigate('/play', { state: { companyId: companyId, tvId: tvIds[parseInt(tvNumber.join('')) - 1] } });
        onClose();
    };

    return open ? (
        <div className="pin-dialog-backdrop">
            <div className="pin-dialog">
                <div className="pin-dialog-content">
                    {step === 'pin' ? (
                        <>
                            <h2>Enter PIN</h2>
                            <div className="pin-input-container">
                                {pinDigits.map((digit, index) => (
                                    <input
                                        key={index}
                                        className="pin-input"
                                        type="tel"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleInputChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e, pinDigits, setPinDigits, 3)}
                                        ref={el => inputRefs.current[index] = el}
                                        disabled={locked}
                                        autoComplete="off"
                                    />
                                ))}
                            </div>
                        </>
                    ) : step === 'tvNumber' ? (
                        <>
                            <h2>Enter TV Number</h2>
                            <div className="pin-input-container">
                                {tvNumber.map((digit, index) => (
                                    <input
                                        key={index}
                                        className="pin-input"
                                        type="tel"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleTvNumberChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e, tvNumber, setTvNumber, 1)}
                                        ref={el => inputRefs.current[index] = el}
                                        disabled={locked}
                                        autoComplete="off"
                                    />
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <h2>Do you want to remember the PIN?</h2>
                            <div className="button-container">
                                <button
                                    autoFocus
                                    className="no-button"
                                    onClick={() => handleRememberChoice(false)}
                                >
                                    No
                                </button>
                                <button
                                    className="yes-button"
                                    onClick={() => handleRememberChoice(true)}
                                >
                                    Yes
                                </button>
                            </div>
                        </>
                    )}
                    {errorMessage && <div className="error-message">{errorMessage}</div>}
                </div>
            </div>
        </div>
    ) : null;
};

export default PinDialog;
